import {
  NAME_MAX,
  dishTotals,
  optionalGramsInput,
  optionalKcalPer100gInput,
  warsawToday,
  type CalorieDishDto,
  type CalorieProductDto,
  type dishBody,
} from '@iac/contracts';
import { useId, useMemo, useState, type FormEvent } from 'react';
import type { z } from 'zod';
import { Badge } from '../../components/Badge';
import { Combobox } from '../../components/Combobox';
import { DeleteButton } from '../../components/DeleteButton';
import { Field } from '../../components/Field';
import {
  IconCalendarPlus,
  IconPencilSquare,
  IconPlus,
  IconTrash,
} from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import { toast } from '../../lib/toast';
import {
  useCreateDish,
  useDeleteDish,
  useDishes,
  useLogDish,
  useProducts,
  useUpdateDish,
} from './api';
import { caloriesDict } from './i18n';
import { productHint } from './links';
import { CatalogTabs } from './Products';
import { SwipeRow } from './SwipeRow';

type DishFormValues = z.input<typeof dishBody>;
type IngredientValues = DishFormValues['ingredients'][number];

/** A form row; `key` keeps React's rows apart while they are added and removed. */
type IngredientRow = IngredientValues & { key: string };

const blankRow = (): IngredientRow => ({
  key: crypto.randomUUID(),
  name: '',
  kcalPer100g: '',
  grams: '',
});

function rowFrom(
  ingredient: CalorieDishDto['ingredients'][number],
): IngredientRow {
  return {
    key: crypto.randomUUID(),
    name: ingredient.name,
    kcalPer100g:
      ingredient.kcalPer100g === null ? '' : String(ingredient.kcalPer100g),
    grams: ingredient.grams === null ? '' : String(ingredient.grams),
  };
}

/** A row left wholly blank is dropped on save rather than refused. */
function isBlank(row: IngredientValues): boolean {
  return (
    row.name.trim() === '' &&
    row.kcalPer100g.trim() === '' &&
    row.grams.trim() === ''
  );
}

/** The live preview, by the same rules the server applies; a bad number counts as missing. */
function previewTotals(rows: IngredientRow[]) {
  return dishTotals(
    rows
      .filter((row) => !isBlank(row))
      .map((row) => {
        const kcalPer100g = optionalKcalPer100gInput.safeParse(row.kcalPer100g);
        const grams = optionalGramsInput.safeParse(row.grams);
        return {
          kcalPer100g: kcalPer100g.success ? kcalPer100g.data : null,
          grams: grams.success ? grams.data : null,
        };
      }),
  );
}

/** "700 g · 219 kcal/100g", "—" while neither is known. */
function totalsText(totals: {
  grams: number | null;
  kcalPer100g: number | null;
}): string {
  return productHint(totals) || '—';
}

/**
 * Add and edit share this form. Only the name is required: a dish can be
 * saved half-written and finished later. Each ingredient is picked from the
 * products (which fills its numbers) or typed; either way it is the dish's
 * own copy.
 */
function DishForm({
  dish,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  dish?: CalorieDishDto;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: DishFormValues, reset: () => void) => void;
  onCancel: () => void;
}) {
  const formId = useId();
  const t = useDict(caloriesDict);
  const { data: products } = useProducts();
  const [name, setName] = useState(dish?.name ?? '');
  const [rows, setRows] = useState<IngredientRow[]>(() =>
    dish && dish.ingredients.length > 0
      ? dish.ingredients.map(rowFrom)
      : [blankRow()],
  );

  // Only a row added by the button takes focus — not the rows the form opens with.
  const [focusKey, setFocusKey] = useState<string | null>(null);

  function addRow() {
    const row = blankRow();
    setRows((current) => [...current, row]);
    setFocusKey(row.key);
  }

  const patchRow = (key: string, next: Partial<IngredientValues>) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, ...next } : row)),
    );

  function pick(key: string, product: CalorieProductDto) {
    patchRow(key, {
      name: product.name,
      kcalPer100g:
        product.kcalPer100g === null ? '' : String(product.kcalPer100g),
      grams: product.grams === null ? '' : String(product.grams),
    });
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit(
      {
        name,
        ingredients: rows
          .filter((row) => !isBlank(row))
          .map(({ name, kcalPer100g, grams }) => ({
            name,
            kcalPer100g,
            grams,
          })),
      },
      () => {
        setName('');
        setRows([blankRow()]);
      },
    );
  }

  const totals = previewTotals(rows);

  return (
    <form onSubmit={submit} className="entry-form">
      <Field
        id={`${formId}-name`}
        label={t.name}
        type="text"
        autoComplete="off"
        required
        maxLength={NAME_MAX}
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <ul className="dish-ingredients">
        {rows.map((row, index) => (
          <li key={row.key} className="dish-ingredient">
            <div className="dish-ingredient-name">
              <Combobox
                id={`${formId}-ingredient-${row.key}`}
                label={`${t.ingredient} ${index + 1}`}
                value={row.name}
                onChange={(value) => patchRow(row.key, { name: value })}
                options={products ?? []}
                optionKey={(p) => p.id}
                optionLabel={(p) => p.name}
                renderOption={(p) => (
                  <>
                    <span>{p.name}</span>
                    <span className="text-hint">{productHint(p)}</span>
                  </>
                )}
                onPick={(product) => pick(row.key, product)}
                maxLength={NAME_MAX}
                autoFocus={row.key === focusKey}
              />
            </div>
            <Field
              id={`${formId}-kcal-${row.key}`}
              label="kcal/100g"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={row.kcalPer100g}
              onChange={(e) =>
                patchRow(row.key, { kcalPer100g: e.target.value })
              }
            />
            <Field
              id={`${formId}-grams-${row.key}`}
              label={t.weight}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={row.grams}
              onChange={(e) => patchRow(row.key, { grams: e.target.value })}
            />
            <button
              type="button"
              className="btn-icon-ghost dish-ingredient-remove"
              aria-label={t.removeIngredient(row.name.trim())}
              title={t.removeIngredient(row.name.trim())}
              onClick={() =>
                setRows((current) => current.filter((r) => r.key !== row.key))
              }
            >
              <IconTrash />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="btn-ghost dish-add-ingredient"
        onClick={addRow}
      >
        <IconPlus /> {t.addIngredient}
      </button>

      <div className="entry-form-footer">
        <span className="entry-form-total">
          {t.dishTotal}: {totalsText(totals)}{' '}
          {totals.incomplete && (
            <Badge tone="warning">{t.dishIncomplete}</Badge>
          )}
        </span>
        <div className="form-actions">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            {t.cancel}
          </button>
          <button type="submit" className="btn-primary" disabled={pending}>
            {submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}

function DishRow({ dish }: { dish: CalorieDishDto }) {
  const t = useDict(caloriesDict);
  const [editing, setEditing] = useState(false);
  const update = useUpdateDish();
  const remove = useDeleteDish();
  const log = useLogDish();

  const confirmDelete = t.confirmDeleteDish(dish.name);
  // Read at the tap, not at render, so a page left open past midnight logs
  // to the new day.
  const logToday = () =>
    log.mutate(
      { id: dish.id, date: warsawToday() },
      {
        onSuccess: () =>
          toast.success(t.loggedToday(dish.name), {
            action: { label: t.viewDay, to: '/calories' },
          }),
      },
    );

  if (editing) {
    return (
      <li className="product-row product-row-form">
        <DishForm
          dish={dish}
          submitLabel={t.save}
          pending={update.isPending}
          onSubmit={(values) =>
            update.mutate(
              { id: dish.id, ...values },
              { onSuccess: () => setEditing(false) },
            )
          }
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <SwipeRow
      className="product-row"
      bodyClassName="product-row-body"
      startLabel={
        <>
          <IconCalendarPlus /> {t.addToToday}
        </>
      }
      deleteLabel={
        <>
          {t.delete} <IconTrash />
        </>
      }
      onRight={() => {
        if (!log.isPending) logToday();
      }}
      onLeft={() => {
        if (window.confirm(confirmDelete)) remove.mutate(dish.id);
      }}
      onClick={() => setEditing(true)}
    >
      <div className="entry-main">
        <p className="entry-name">
          {dish.name}{' '}
          {dish.incomplete && <Badge tone="warning">{t.dishIncomplete}</Badge>}
        </p>
        {dish.grams !== null && <p className="entry-detail">{dish.grams} g</p>}
      </div>
      <p className="product-kcal">{dish.kcalPer100g ?? '—'}</p>
      {/* Desktop only; a phone swipes or taps instead. Clicks here mustn't
            also open the editor. */}
      <div className="product-actions" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="btn-icon-ghost"
          aria-label={t.addToTodayItem(dish.name)}
          title={t.addToToday}
          disabled={log.isPending}
          onClick={logToday}
        >
          <IconCalendarPlus />
        </button>
        <button
          type="button"
          className="btn-icon-ghost"
          aria-label={t.editItem(dish.name)}
          title={t.edit}
          onClick={() => setEditing(true)}
        >
          <IconPencilSquare />
        </button>
        <DeleteButton
          label={t.deleteItem(dish.name)}
          confirm={confirmDelete}
          onConfirm={() => remove.mutate(dish.id)}
        />
      </div>
    </SwipeRow>
  );
}

/**
 * The dish catalog, A–Z — the Products page's second tab. "Add to today"
 * logs one portion, outside any meal, on today, as a plain entry.
 */
export function CaloriesDishes() {
  const t = useDict(caloriesDict);
  usePageTitle(`${t.dishes} — ${t.titleSuffix}`);
  const formId = useId();
  const { data: dishes } = useDishes();
  const create = useCreateDish();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');

  const shown = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('pl');
    return q === ''
      ? dishes
      : dishes?.filter((d) => d.name.toLocaleLowerCase('pl').includes(q));
  }, [dishes, search]);

  return (
    <>
      <PageHeader
        title={t.products}
        actions={
          !adding && (
            <button
              type="button"
              className="btn-primary-icon"
              aria-label={t.newDish}
              title={t.newDish}
              onClick={() => setAdding(true)}
            >
              <IconPlus />
            </button>
          )
        }
      />
      <CatalogTabs />

      {adding && (
        <Panel title={t.newDish} tone="highlight">
          <DishForm
            submitLabel={t.add}
            pending={create.isPending}
            onSubmit={(values, reset) =>
              create.mutate(values, { onSuccess: reset })
            }
            onCancel={() => setAdding(false)}
          />
        </Panel>
      )}

      <Panel>
        <Field
          id={`${formId}-search`}
          label={t.search}
          type="search"
          autoComplete="off"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Panel>

      <Panel className="product-list-card">
        {shown && shown.length > 0 ? (
          <>
            <div className="product-list-header" aria-hidden="true">
              <span className="entry-main">{t.dish}</span>
              <span className="product-list-header-kcal">kcal/100g</span>
              <span className="product-actions-spacer" />
            </div>
            <ul className="product-list">
              {shown.map((dish) => (
                <DishRow key={dish.id} dish={dish} />
              ))}
            </ul>
          </>
        ) : (
          <p className="product-list-empty">
            {dishes && dishes.length > 0 ? t.noMatch : t.dishesEmpty}
          </p>
        )}
      </Panel>
    </>
  );
}
